import type { FieldlessCTAContent } from '@/components/Elements/CTA';
import {
  ContentStyle,
  type FieldlessDropdownMenu,
  type FieldlessDropdownMenuItem,
  type GlobalNavContent,
} from '@/contentful/contentful.types';
import { useGetConstrainedContent } from '@/hooks/useGetConstrainedContent';
import { makeStyles } from '@/styles/theme/tss-react';
import type {
  FeatureCardCF,
  HeaderMenuItemCF,
  LinkCF,
  SectionConstraintsCF,
} from '@/types';
import {
  isTypeFeatureCard,
  isTypeHeaderMenuItem,
  isTypeLink,
  isTypeSectionConstraints,
} from '@/types/contentful';
import DesktopNav from './DesktopNav/DesktopNav';
import MobileNav from './MobileNav/MobileNav';

export interface GlobalNavigationProps {
  categories: FieldlessDropdownMenu[];
  promoCta?: FieldlessCTAContent;
  loginCta: FieldlessCTAContent;
  primaryCta: FieldlessCTAContent;
  secondaryCta?: FieldlessCTAContent;
  style: ContentStyle;
  immersive?: boolean;
}

export enum NavPosition {
  FIXED = 'fixed',
  STATIC = 'static',
  ABSOLUTE = 'absolute',
}

export const getPosition = (navState: NavPosition) => {
  switch (navState) {
    case NavPosition.ABSOLUTE:
      return 'absolute';
    case NavPosition.FIXED:
      return 'fixed';
    default:
      return 'static';
  }
};

export const GlobalNavigationWrapper = ({
  content,
}: {
  content: GlobalNavContent;
}) => {
  const getConstrainedContent = useGetConstrainedContent();
  const categories: FieldlessDropdownMenu[] = content.fields.categories.map(
    (category) => {
      const featureCard = category.fields.featured;

      let constrainedFeatureCard: FeatureCardCF | undefined;
      if (isTypeFeatureCard(featureCard)) {
        constrainedFeatureCard = featureCard;
      } else if (isTypeSectionConstraints(featureCard)) {
        constrainedFeatureCard = getConstrainedContent<FeatureCardCF>({
          constrainedSection: featureCard,
          include: ['featureCard'],
        })?.[0];
      }

      return {
        links: category.fields.links.flatMap((link) => {
          if (isTypeHeaderMenuItem(link)) {
            return [{ ...link.fields } as FieldlessDropdownMenuItem];
          }

          if (isTypeSectionConstraints(link)) {
            const item = getConstrainedContent<HeaderMenuItemCF>({
              constrainedSection: link,
              include: ['headerMenuItem'],
            })?.[0];
            return item
              ? [{ ...item.fields } as FieldlessDropdownMenuItem]
              : [];
          }

          return [];
        }),
        name: category.fields.name,
        ...(constrainedFeatureCard && {
          featured: { ...constrainedFeatureCard.fields },
        }),
      };
    }
  );

  const promoCta = content.fields?.promoCta;
  let constrainedPromoCta: LinkCF | undefined;
  if (promoCta) {
    if (isTypeLink(promoCta)) {
      constrainedPromoCta = promoCta;
    } else if (isTypeSectionConstraints(promoCta)) {
      constrainedPromoCta = getConstrainedContent<LinkCF>({
        constrainedSection: content.fields?.promoCta as SectionConstraintsCF,
        include: ['link'],
      })?.[0];
    }
  }

  const secondaryCta = content.fields?.secondaryCta
    ? { ...content.fields.secondaryCta.fields }
    : undefined;

  const props: GlobalNavigationProps = {
    categories,
    ...(constrainedPromoCta && { promoCta: constrainedPromoCta.fields }),
    secondaryCta,
    loginCta: { ...content.fields.loginCta.fields },
    primaryCta: { ...content.fields.primaryCta.fields },
    style: content.fields.style,
    immersive: content.fields.immersive,
  };

  return <GlobalNav {...props} />;
};

// Render both navs and toggle visibility with CSS media queries (md = 1024px,
// matching the previous JS `isExtraSmall || isSmall` boundary). A JS-based
// conditional render can't know the viewport at SSR/first paint, so it always
// rendered MobileNav first and then swapped to DesktopNav after mount, causing
// a layout shift (CLS) for every desktop visitor.
const useStyles = makeStyles({ name: 'GlobalNavigation' })(
  ({ breakpoints }) => ({
    mobileOnly: {
      [breakpoints.up('md')]: {
        display: 'none',
      },
    },
    desktopOnly: {
      display: 'none',
      [breakpoints.up('md')]: {
        display: 'block',
      },
    },
  })
);

const GlobalNav = (props: GlobalNavigationProps) => {
  const { classes } = useStyles();
  const isOnDark = props.style === ContentStyle.OnDark;

  return (
    <header>
      <div className={classes.mobileOnly} data-testid='mobile-nav-wrapper'>
        <MobileNav {...props} isOnDark={isOnDark} />
      </div>
      <div className={classes.desktopOnly} data-testid='desktop-nav-wrapper'>
        <DesktopNav {...props} isOnDark={isOnDark} />
      </div>
    </header>
  );
};

export default GlobalNav;
